import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { MATERIAL_COLORS } from './tools/PolylineTool';
import {
  WrapperS,
  TabsS,
  TabS,
  WhiteBoardS,
  ButtonS,
  ToolbarS,
  ZoomBarS,
  ColorBarS,
  ToolbarItemS,
  RangeInputS,
  ColorButtonS,
  SeparatorS,
  ToolbarHolderS,
  PDFWrapperS,
  LogoWrapperS,
} from './Whiteboard.styled.js';
import { fabric } from 'fabric';
import { PdfReader } from '../PdfReader';
import { saveAs } from 'file-saver';
import { Board, modes } from './Board.Class.js';
import { ColorPicker } from '../ColorPicker';
import ContextPanel from './ContextPanel';

import SelectIcon from './../images/cross.svg';
import EraserIcon from './../images/eraser.svg';
import TextIcon from './../images/text.svg';
import RectangleIcon from './../images/rectangle.svg';
import LineIcon from './../images/line.svg';
import EllipseIcon from './../images/ellipse.svg';
import TriangleIcon from './../images/triangle.svg';
import PencilIcon from './../images/pencil.svg';
import DeleteIcon from './../images/delete.svg';
import ZoomInIcon from './../images/zoom-in.svg';
import ZoomOutIcon from './../images/zoom-out.svg';
import DownloadIcon from './../images/download.svg';
import UploadIcon from './../images/add-photo.svg';
import FillIcon from './../images/color-fill.svg';
import Recenter from './../images/focus.svg';
import ScaleIcon from './../images/scale.svg';
import PanIcon from './../images/pan.png';
import DimensionIcon from './../images/dimension.svg';
import PolylineIcon from './../images/polyline.svg';
import UndoIcon from './../images/undo.svg';
import RedoIcon from './../images/redo.svg';
import MaterialCalculatorIcon from './../images/material-calculator.svg';
import ObjectCalculatorIcon from './../images/object-calculator.svg';
import StampIcon from './../images/stamp.svg';
import { StampPalette } from './StampPalette';
import { ObjectCalculator } from './ObjectCalculator';

const initFileInfo = {
  file: { name: 'Teempla' },
  totalPages: 1,
  currentPageNumber: 1,
  currentPage: '',
};

const initDrawingSettings = {
  brushWidth: 2,
  currentMode: 'PENCIL',
  currentColor: '#FF0000FF',
  fill: false,
  // background: true,
};

const initSettings = {
  zoom: 1,
  contentJSON: null,
};
const defaultFunction = (data, event, canvas) => {};

const Whiteboard = ({
  controls,
  settings,
  drawingSettings,
  fileInfo,
  imageSlot,
  onObjectAdded = defaultFunction,
  onObjectRemoved = defaultFunction,
  onObjectModified = defaultFunction,
  onCanvasRender = defaultFunction,
  onCanvasChange = defaultFunction,
  onZoom = defaultFunction,
  onImageUploaded = defaultFunction,
  onPDFUploaded = defaultFunction,
  onPDFUpdated = defaultFunction,
  onPageChange = defaultFunction,
  onOptionsChange = defaultFunction,
  onSaveCanvasAsImage = defaultFunction,
  onConfigChange = defaultFunction,
  onSaveCanvasState = defaultFunction,
  onDocumentChanged = defaultFunction,
}) => {
  const [canvasSaveData, setCanvasSaveData] = useState([]);
  const [board, setBoard] = useState();
  const [canvasObjectsPerPage, setCanvasObjectsPerPage] = useState({});
  const [canvasDrawingSettings, setCanvasDrawingSettings] = useState({
    ...initDrawingSettings,
    ...drawingSettings,
  });
  const canvasConfig = { ...initSettings, ...settings };
  const [documents, setDocuments] = useState(
    new Map().set(initFileInfo.file.name, initFileInfo.file),
  );
  const [zoom, setZoom] = useState(canvasConfig.zoom);
  const [fileReaderInfo, setFileReaderInfo] = useState({ ...initFileInfo, ...fileInfo });
  const [contextPanel, setContextPanel] = useState({ visible: false, x: 0, y: 0, selectedObject: null });
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [hasSelectedObjects, setHasSelectedObjects] = useState(false);
  const [selectedStamp, setSelectedStamp] = useState(null);
  const canvasRef = useRef(null);
  const whiteboardRef = useRef(null);
  const uploadPdfRef = useRef(null);
  const boardRef = useRef(null);

  const enabledControls = useMemo(
    function () {
      return {
        [modes.PENCIL]: true,
        [modes.LINE]: true,
        [modes.RECTANGLE]: true,
        [modes.ELLIPSE]: true,
        [modes.TRIANGLE]: true,
        [modes.TEXT]: true,
        [modes.SELECT]: true,
        [modes.ERASER]: true,
        [modes.SCALE]: true,
        [modes.DIMENSION]: true,
        [modes.POLYLINE]: true,
        [modes.PAN]: true,
        [modes.MATERIAL_CALCULATOR]: true,
        [modes.STAMP]: true,
        [modes.OBJECT_CALCULATOR]: true,
        CLEAR: true,
        FILL: true,
        BRUSH: true,
        COLOR_PICKER: true,
        DEFAULT_COLORS: true,
        FILES: true,
        SAVE_AS_IMAGE: true,
        GO_TO_START: true,
        SAVE_AND_LOAD: true,
        ZOOM: true,
        TABS: true,
        UNDO_REDO: true,

        ...controls,
      };
    },
    [controls],
  );

  useEffect(() => {
    setCanvasDrawingSettings({ ...canvasDrawingSettings, ...drawingSettings });
  }, [drawingSettings]);
  useEffect(() => {
    if (imageSlot) {
      fileChanger(imageSlot);
    }
  }, [imageSlot]);

  useEffect(() => {
    if (!board || !canvasConfig) return;
    board.setCanvasConfig(canvasConfig);
  }, [settings]);

  useEffect(() => {
    setFileReaderInfo({ ...fileReaderInfo, ...fileInfo });
  }, [fileInfo]);

  useEffect(() => {
    if (board) {
      return;
    }

    const newBoard = new Board({
      drawingSettings: canvasDrawingSettings,
      canvasConfig: canvasConfig,
      canvasRef: canvasRef, // Sketch range limits
    });

    // Set fileReaderInfo on the board
    if (fileReaderInfo) {
      newBoard.setFileReaderInfo(fileReaderInfo);
    }
    
    // Set up context menu callback
    newBoard.setContextMenuCallback(setContextPanel);

    setBoard(newBoard);
    boardRef.current = newBoard;
    addListeners(newBoard.canvas);

    return () => {
      if (board) {
        board.removeBoard();
      }
    };
  }, [board, fileReaderInfo]);

  useEffect(() => {
    if (!board || !canvasDrawingSettings) return;

    board.setDrawingSettings(canvasDrawingSettings);
    
    // Reset selected stamp when changing modes away from STAMP
    if (canvasDrawingSettings.currentMode !== modes.STAMP) {
      setSelectedStamp(null);
    }
  }, [canvasDrawingSettings, board]);
  
  // Effect to handle stamp selection when stamp tool is active
  useEffect(() => {
    if (!board || canvasDrawingSettings.currentMode !== modes.STAMP) return;
    
    // Set the selected stamp in the tool when it changes
    if (board.currentTool && selectedStamp) {
      board.currentTool.setCurrentStamp(selectedStamp);
    }
  }, [selectedStamp, board, canvasDrawingSettings.currentMode]);
  
  // Add keyboard shortcuts for undo/redo
  // Function to update undo/redo availability states
  const updateUndoRedoState = useCallback(() => {
    if (!board || !board.history) return;
    
    const historyLength = board.history.getHistoryLength();
    const redoHistoryLength = board.history.getRedoHistoryLength();
    
    // We need at least one history item to undo
    setCanUndo(historyLength > 0);
    setCanRedo(redoHistoryLength > 0);
    
    if (process.env.NODE_ENV !== 'production') {
      console.log(`History state updated - history length: ${historyLength}, redo length: ${redoHistoryLength}, canUndo: ${historyLength > 0}, canRedo: ${redoHistoryLength > 0}`);
    }
  }, [board]);
  
  // Monitor canvas for changes that should update undo/redo state
  useEffect(() => {
    if (!board || !board.canvas) return;
    
    // Initial update
    updateUndoRedoState();
    
    // Create a listener for history changes
    const handleHistoryChange = () => {
      updateUndoRedoState();
    };
    
    // Listen for history-related events from the canvas
    board.canvas.on('history:changed', handleHistoryChange);
    
    return () => {
      // Clean up event listeners when component unmounts
      board.canvas.off('history:changed', handleHistoryChange);
    };
  }, [board, updateUndoRedoState]);
  
  // Handler for undo action with debouncing logic
  const [isUndoing, setIsUndoing] = useState(false);
  const [isRedoing, setIsRedoing] = useState(false);
  
  const handleUndo = useCallback(() => {
    if (!board || !canUndo || isUndoing) return;
    
    setIsUndoing(true);
    
    try {
      // Execute the undo operation
      board.undo();
      
      // Give the canvas time to update before changing state
      setTimeout(() => {
        updateUndoRedoState();
        setIsUndoing(false);
      }, 100);
    } catch (error) {
      console.error('Error during undo operation:', error);
      setIsUndoing(false);
      updateUndoRedoState();
    }
  }, [board, canUndo, isUndoing, updateUndoRedoState]);
  
  // Handler for redo action with debouncing logic
  const handleRedo = useCallback(() => {
    if (!board || !canRedo || isRedoing) return;
    
    setIsRedoing(true);
    
    try {
      // Execute the redo operation
      board.redo();
      
      // Give the canvas time to update before changing state
      setTimeout(() => {
        updateUndoRedoState();
        setIsRedoing(false);
      }, 100);
    } catch (error) {
      console.error('Error during redo operation:', error);
      setIsRedoing(false);
      updateUndoRedoState();
    }
  }, [board, canRedo, isRedoing, updateUndoRedoState]);

  useEffect(() => {
    if (!board?.canvas) {
      return;
    }

    // Update the board's fileReaderInfo using the setter method
    board.setFileReaderInfo(fileReaderInfo);
    
    const json = getPageJSON({
      fileName: fileReaderInfo.file.name,
      pageNumber: fileReaderInfo.currentPageNumber,
    });
    if (json) {
      board.canvas.loadFromJSON(json);
      
      // After loading JSON, filter any info windows by page
      const polylineTool = board.toolFactory?.getTool('POLYLINE');
      if (polylineTool) {
        polylineTool.filterInfoWindowsByPage(
          fileReaderInfo.currentPageNumber, 
          fileReaderInfo.file?.name
        );
      }
    } else {
      board.openPage(fileReaderInfo.currentPage);
    }
  }, [fileReaderInfo?.file?.name, fileReaderInfo.currentPage]);

  function uploadImage(e) {
    const reader = new FileReader();
    const file = e.target.files[0];

    reader.addEventListener('load', () => {
      fabric.Image.fromURL(reader.result, (img) => {
        img.scaleToHeight(board.canvas.height);
        board.canvas.add(img);
      });
    });

    reader.readAsDataURL(file);
  }
  function uploadImageFile(file) {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      fabric.Image.fromURL(reader.result, (img) => {
        img.scaleToHeight(board?.canvas?.height);
        board.canvas.add(img);
      });
    });

    reader.readAsDataURL(file);
  }

  function addListeners(canvas) {
    canvas.on('after:render', (e) => {
      const data = getFullData(canvas);
      onCanvasRender(data, e, canvas);
    });

    canvas.on('zoom:change', function (data) {
      onZoom(data, null, canvas);
      setZoom(data.scale);
    });

    canvas.on('object:added', (event) => {
      onObjectAdded(event.target.toJSON(), event, canvas);
      onCanvasChange(event.target.toJSON(), event, canvas);
      // Update undo/redo buttons state after adding an object
      setTimeout(updateUndoRedoState, 100);
    });

    canvas.on('object:removed', (event) => {
      onObjectRemoved(event.target.toJSON(), event, canvas);
      onCanvasChange(event.target.toJSON(), event, canvas);
      // Update undo/redo buttons state after removing an object
      setTimeout(updateUndoRedoState, 100);
    });

    canvas.on('object:modified', (event) => {
      onObjectModified(event.target.toJSON(), event, canvas);
      onCanvasChange(event.target.toJSON(), event, canvas);
      // Update undo/redo buttons state after modifying an object
      setTimeout(updateUndoRedoState, 100);
    });
    
    // Track selection events to update the selected objects state
    canvas.on('selection:created', () => {
      setHasSelectedObjects(true);
    });
    
    canvas.on('selection:updated', () => {
      setHasSelectedObjects(true);
    });
    
    canvas.on('selection:cleared', () => {
      setHasSelectedObjects(false);
    });
  }

  function handleSaveCanvasState() {
    const newCanvasState = board.canvas.toJSON();
    setCanvasSaveData((prevStates) => [...prevStates, newCanvasState]);
  }

  function handleLoadCanvasState(state) {
    if (board && state) {
      board.canvas.loadFromJSON(state, () => {
        board.canvas.renderAll();
      });
    }
  }

  function getFullData(canvas) {
    if (!canvas) return;

    return {
      settings: {
        contentJSON: canvas.toJSON(),
        viewportTransform: canvas.viewportTransform,
      },
      drawingSettings: canvasDrawingSettings,
      fileInfo: fileReaderInfo,
    };
  }

  function saveCanvasState() {
    // Explicitly include custom properties in the JSON serialization
    const customProperties = ['material', 'polyline', 'pageNumber', 'documentName', 'area', 'perimeter', 'scaleRatio'];
    
    // Use toJSON with custom property array to ensure all important properties are saved
    const newValue = {
      ...canvasObjectsPerPage,
      [fileReaderInfo.file.name]: {
        ...canvasObjectsPerPage[fileReaderInfo.file.name],
        [fileReaderInfo.currentPageNumber]: board.canvas.toJSON(customProperties),
      },
    };
    
    // Debug info to verify materials are being saved properly
    const polygons = board.canvas.getObjects().filter(obj => 
      obj.polyline === true || obj.type === 'polygon'
    );
    
    console.log(`Saving ${polygons.length} polygons with the following materials:`);
    polygons.forEach(p => console.log(`  - ${p.id || 'unknown'}: ${p.material || 'NO MATERIAL'}`));
    
    setCanvasObjectsPerPage(newValue);
    onSaveCanvasState(newValue);
  }

  function changeBrushWidth(e) {
    const intValue = parseInt(e.target.value);
    board.canvas.freeDrawingBrush.width = intValue;
    const newOptions = { ...canvasDrawingSettings, brushWidth: intValue };
    setCanvasDrawingSettings(newOptions);
    onOptionsChange(newOptions, e, board.canvas);
  }

  function changeMode(mode, e) {
    if (canvasDrawingSettings.currentMode === mode) return;
    const newOptions = { ...canvasDrawingSettings, currentMode: mode };
    setCanvasDrawingSettings(newOptions);
    onOptionsChange(newOptions, e, board.canvas);
  }

  function changeCurrentColor(color, e) {
    board.canvas.freeDrawingBrush.color = color;
    const newOptions = { ...canvasDrawingSettings, currentColor: color };
    setCanvasDrawingSettings(newOptions);
    onOptionsChange(newOptions, e, board.canvas);
  }

  function changeFill(e) {
    const newOptions = { ...canvasDrawingSettings, fill: !canvasDrawingSettings.fill };
    setCanvasDrawingSettings(newOptions);
    onOptionsChange(newOptions, e, board.canvas);
  }

  function handleSaveCanvasAsImage() {
    canvasRef.current.toBlob(function (blob) {
      saveAs(
        blob,
        `${fileReaderInfo.file.name}-${fileReaderInfo.currentPageNumber ? '_page-' : ''}.png`,
      );
      onSaveCanvasAsImage(blob, null, board.canvas);
    });
  }

  function bringControlTOStartPosition() {
    board.canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
    board.resetZoom(1);

    board.nowX = 0;
    board.nowY = 0;
  }

  function onFileChange(event) {
    const file = event.target?.files?.[0];
    if (!file) return;

    if (file.type.includes('image/')) {
      uploadImage(event);
      onImageUploaded(file, event, board.canvas);
    } else if (file.type.includes('pdf')) {
      saveCanvasState();
      board.clearCanvas();
      updateFileReaderInfo({ file: file, currentPageNumber: 1 });
      setDocuments((prev) => new Map(prev.set(file.name, file)));
      onPDFUploaded(file, event, board.canvas);
    }
  }
  function fileChanger(file) {
    if (file.type.includes('image/')) {
      uploadImageFile(file);
    } else if (file.type.includes('pdf')) {
      saveCanvasState();
      board.clearCanvas();
      updateFileReaderInfo({ file: file, currentPageNumber: 1 });
      setDocuments((prev) => new Map(prev.set(file.name, file)));
    }
  }

  function getPageJSON({ fileName, pageNumber }) {
    if (canvasObjectsPerPage[fileName] && canvasObjectsPerPage[fileName][pageNumber]) {
      return canvasObjectsPerPage[fileName][pageNumber];
    } else {
      return null;
    }
  }

  function updateFileReaderInfo(data) {
    const newFileData = { ...fileReaderInfo, ...data };
    setFileReaderInfo(newFileData);
    onPDFUpdated(newFileData, null, board.canvas);
  }

  const handlePageChange = (page) => {
    // Save the current page state first
    saveCanvasState();
    
    // Update the board's fileReaderInfo using the setter method
    const newFileInfo = { ...fileReaderInfo, currentPageNumber: page };
    
    console.log(`Page change: from page ${fileReaderInfo.currentPageNumber} to page ${page}`);
    
    // IMPORTANT: We need to completely clear the canvas when switching pages
    // This ensures proper page separation (each page is treated separately)
    board.clearCanvas(board.canvas);
    
    // Set new file info after clearing
    board.setFileReaderInfo(newFileInfo);
    setFileReaderInfo(newFileInfo);
    
    // Check if we have saved objects for the new page
    const hasPageData = canvasObjectsPerPage[fileReaderInfo.file.name]?.[page];
    
    if (hasPageData) {
      console.log(`Found saved data for page ${page}, loading ${hasPageData.objects?.length || 0} objects`);
      // Load the saved state for this page
      const savedPage = canvasObjectsPerPage[fileReaderInfo.file.name][page];
      
      // Load saved JSON state, then ensure properties are correctly set
      board.canvas.loadFromJSON(savedPage, () => {
        // After loading, validate every object to ensure proper properties
        const objects = board.canvas.getObjects();
        objects.forEach(obj => {
          // For polygons, ensure material and page info is correct
          if (obj.polyline === true || obj.type === 'polygon') {
            // Ensure polygon has correct page info
            if (obj.pageNumber !== page || obj.documentName !== fileReaderInfo.file.name) {
              obj.set({
                pageNumber: page,
                documentName: fileReaderInfo.file.name
              });
            }
            
            // CRITICAL FIX: Reconstruct material property if missing but fill color exists
            // This ensures material name is preserved even if JSON serialization loses it
            if (!obj.material && obj.fill) {
              // Look up the material by its color
              for (const [material, materialColor] of Object.entries(MATERIAL_COLORS)) {
                if (materialColor === obj.fill) {
                  console.log(`Restoring material '${material}' from fill color ${obj.fill}`);
                  obj.set('material', material);
                  break;
                }
              }
            }
            // Make fill color match material (if material exists)
            else if (obj.material) {
              const materialColor = MATERIAL_COLORS[obj.material];
              if (materialColor && obj.fill !== materialColor) {
                obj.set('fill', materialColor);
              }
            }
            
            // Debug log to confirm material property
            console.log(`Object after restoration: id=${obj.id || 'unknown'}, ` +
                       `material=${obj.material || 'NONE'}, fill=${obj.fill}`);
          }
        });
        
        board.canvas.renderAll();
        console.log(`Loaded and validated ${objects.length} objects for page ${page}`);
      });
    }
    
    // Trigger page change callback last
    onPageChange(newFileInfo, null, board.canvas);
  };

  const changeDocument = (name) => {
    bringControlTOStartPosition();
    saveCanvasState();
    
    // Update the board's fileReaderInfo using the setter method
    const newFileInfo = { file: documents.get(name), currentPageNumber: 1 };
    
    console.log(`Document change: from ${fileReaderInfo.file?.name} to ${name}`);
    
    // IMPORTANT: Always clear the canvas when switching documents
    // This ensures proper page/document separation
    board.clearCanvas(board.canvas);
    
    // Set new file info after clearing
    board.setFileReaderInfo(newFileInfo);
    setFileReaderInfo(newFileInfo);
    
    // Check if we have saved objects for the first page of the new document
    const hasDocumentData = canvasObjectsPerPage[name]?.[1];
    
    if (hasDocumentData) {
      console.log(`Found saved data for document ${name}, page 1`);
      // Load the saved state for the first page
      const savedPage = canvasObjectsPerPage[name][1];
      
      // Load saved JSON state, then validate all objects
      board.canvas.loadFromJSON(savedPage, () => {
        // Validate every object to ensure proper properties
        const objects = board.canvas.getObjects();
        objects.forEach(obj => {
          // For polygons, ensure material and page info is correct
          if (obj.polyline === true || obj.type === 'polygon') {
            // Ensure polygon has correct page and document info
            if (obj.pageNumber !== 1 || obj.documentName !== name) {
              obj.set({
                pageNumber: 1,
                documentName: name
              });
            }
            
            // CRITICAL FIX: Reconstruct material property if missing but fill color exists
            // This ensures material name is preserved even if JSON serialization loses it
            if (!obj.material && obj.fill) {
              // Look up the material by its color
              for (const [material, materialColor] of Object.entries(MATERIAL_COLORS)) {
                if (materialColor === obj.fill) {
                  console.log(`Restoring material '${material}' from fill color ${obj.fill}`);
                  obj.set('material', material);
                  break;
                }
              }
            }
            // Make fill color match material (if material exists)
            else if (obj.material) {
              const materialColor = MATERIAL_COLORS[obj.material];
              if (materialColor && obj.fill !== materialColor) {
                obj.set('fill', materialColor);
              }
            }
            
            // Debug log to confirm material property
            console.log(`Object after restoration: id=${obj.id || 'unknown'}, ` +
                       `material=${obj.material || 'NONE'}, fill=${obj.fill}`);
          }
        });
        
        board.canvas.renderAll();
        console.log(`Loaded and validated ${objects.length} objects for document ${name}, page 1`);
      });
    }
    
    // Trigger document change callback
    onDocumentChanged(newFileInfo, null, board.canvas);
  };

  const handleZoomIn = () => {
    board.changeZoom({ scale: 1.1 });
  };

  const handleZoomOut = () => {
    board.changeZoom({ scale: 0.9 });
  };

  const handleResetZoom = () => {
    board.resetZoom(1);
  };
  
  const handlePropertyChange = (property, value, additionalData) => {
    if (!board) return;
    
    try {
      console.log(`Handling property change: ${property} with value:`, value);
      
      // Get the active object from the canvas
      const activeObject = board.canvas.getActiveObject();
      if (!activeObject) {
        console.warn('No active object to apply property changes to');
        return;
      }
      
      switch (property) {
        case 'stroke': // Color from color picker
          if (board.changeSelectedObjectColor) {
            board.changeSelectedObjectColor(value);
          } else {
            // Direct application if method not available
            if (activeObject.type === 'group') {
              activeObject._objects.forEach(obj => {
                if (obj.stroke) obj.set('stroke', value);
                // Update fill too if it's not transparent
                if (obj.fill && obj.fill !== 'transparent') obj.set('fill', value);
              });
            } else {
              if (activeObject.stroke) activeObject.set('stroke', value);
              // Update fill too if it's not transparent
              if (activeObject.fill && activeObject.fill !== 'transparent') activeObject.set('fill', value);
            }
            board.canvas.requestRenderAll();
            board.history?.snapshot();
          }
          break;
        case 'strokeWidth': // Width from slider
          if (board.changeSelectedObjectWidth) {
            board.changeSelectedObjectWidth(value);
          } else {
            // Direct application if method not available
            if (activeObject.type === 'group') {
              activeObject._objects.forEach(obj => {
                if (obj.strokeWidth !== undefined) obj.set('strokeWidth', value);
              });
            } else {
              if (activeObject.strokeWidth !== undefined) activeObject.set('strokeWidth', value);
            }
            board.canvas.requestRenderAll();
            board.history?.snapshot();
          }
          break;
        case 'opacity': // Opacity from slider
          // The opacity comes as a decimal value (0-1) from ContextPanel
          console.log('Setting opacity to:', value, 'on object type:', activeObject.type);
          
          // Make sure we have a valid opacity value (between 0 and 1)
          const safeOpacity = Math.max(0.05, Math.min(1, value)); // Ensure at least 5% opacity
          
          // This is important: Board.changeSelectedObjectOpacity now expects a value in 0-1 range, NOT a percentage
          if (board.changeSelectedObjectOpacity) {
            // Pass the direct decimal value (don't divide by 100 again)
            board.changeSelectedObjectOpacity(safeOpacity);
          } else {
            // Direct application if method not available
            if (activeObject.type === 'group') {
              activeObject._objects.forEach(obj => {
                obj.set('opacity', safeOpacity);
              });
              activeObject.set('opacity', safeOpacity); // Set group opacity too
            } else {
              activeObject.set('opacity', safeOpacity);
            }
            board.canvas.requestRenderAll();
            board.history?.snapshot();
          }
          break;
        case 'fill': // Fill toggle
          if (board.toggleSelectedObjectFill) {
            board.toggleSelectedObjectFill();
          } else {
            // Direct application if method not available
            if (activeObject.type === 'group') {
              activeObject._objects.forEach(obj => {
                if (value === null) {
                  obj.set('fill', 'transparent');
                } else if (obj.stroke) {
                  obj.set('fill', obj.stroke);
                }
              });
            } else {
              if (value === null) {
                activeObject.set('fill', 'transparent');
              } else if (activeObject.stroke) {
                activeObject.set('fill', activeObject.stroke);
              }
            }
            board.canvas.requestRenderAll();
            board.history?.snapshot();
          }
          break;
        case 'material': // Material selection for polylines
          if (board.setSelectedObjectMaterial) {
            board.setSelectedObjectMaterial(value, additionalData);
          } else {
            // Direct application if method not available
            if (activeObject.type === 'group') {
              activeObject._objects.forEach(obj => {
                if (obj.polyline) {
                  obj.set({
                    'material': value,
                    'fill': additionalData // Set the fill color based on material
                  });
                }
              });
            } else if (activeObject.polyline) {
              activeObject.set({
                'material': value,
                'fill': additionalData // Set the fill color based on material
              });
            }
            board.canvas.requestRenderAll();
            board.history?.snapshot();
          }
          break;
        default:
          console.warn('Unknown property change:', property);
      }
      
      // Update the context panel to reflect the changes
      if (board.contextMenuCallback) {
        const selectedObject = board.getSelectedObjectProperties();
        board.contextMenuCallback({
          visible: true,
          x: 0,
          y: 0,
          selectedObject
        });
      }
    } catch (error) {
      console.error('Error in handlePropertyChange:', error);
    }
  };
  
  const handleLayerAction = (action) => {
    if (!board) return;
    
    switch (action) {
      case 'bringForward':
        board.bringSelectedForward();
        break;
      case 'sendBackward':
        board.sendSelectedBackward();
        break;
      case 'bringToFront':
        board.bringSelectedToFront();
        break;
      case 'sendToBack':
        board.sendSelectedToBack();
        break;
      case 'delete':
        if (board.deleteSelectedObjects()) {
          // Close context panel after deletion
          setContextPanel((prev) => ({ ...prev, visible: false }));
          // Update undo/redo state
          updateUndoRedoState();
        }
        break;
      default:
        console.warn('Unknown layer action:', action);
    }
  };

  const getColorButtons = (colors) => {
    return colors.map((color) => (
      <ToolbarItemS key={color}>
        <ColorButtonS color={color} onClick={(e) => changeCurrentColor(color, e)} />
      </ToolbarItemS>
    ));
  };

  const getControls = () => {
    const modeButtons = {
      [modes.PENCIL]: { icon: PencilIcon, name: 'Pencil' },
      [modes.LINE]: { icon: LineIcon, name: 'Line' },
      [modes.RECTANGLE]: { icon: RectangleIcon, name: 'Rectangle' },
      [modes.ELLIPSE]: { icon: EllipseIcon, name: 'Ellipse' },
      [modes.TRIANGLE]: { icon: TriangleIcon, name: 'Triangle' },
      [modes.TEXT]: { icon: TextIcon, name: 'Text' },
      [modes.SELECT]: { icon: SelectIcon, name: 'Select' },
      [modes.ERASER]: { icon: EraserIcon, name: 'Eraser' },
      [modes.SCALE]: { icon: ScaleIcon, name: 'Scale' },
      [modes.DIMENSION]: { icon: DimensionIcon, name: 'Dimension' },
      [modes.POLYLINE]: { icon: PolylineIcon, name: 'Polyline' },
      [modes.PAN]: { icon: PanIcon, name: 'Pan Tool' },
      [modes.MATERIAL_CALCULATOR]: { icon: MaterialCalculatorIcon, name: 'Material Calculator' },
      [modes.STAMP]: { icon: StampIcon, name: 'Stamp Objects' },
      [modes.OBJECT_CALCULATOR]: { icon: ObjectCalculatorIcon, name: 'Object Calculator' },
    };

    return Object.keys(modeButtons).map((buttonKey) => {
      if (!enabledControls[buttonKey]) return;
      const btn = modeButtons[buttonKey];
      // Special handling for select mode to include deletion info in tooltip
      const buttonTitle = buttonKey === modes.SELECT
        ? 'Select/Move Objects (Delete or Backspace key to remove selected objects)'
        : btn.name;
      
      return (
        <ButtonS
          key={buttonKey}
          type="button"
          className={`${canvasDrawingSettings.currentMode === buttonKey ? 'selected' : ''}`}
          onClick={(e) => changeMode(buttonKey, e)}
          title={buttonTitle}
        >
          <img src={btn.icon} alt={btn.name} />
        </ButtonS>
      );
    });
  };

  return (
    <WrapperS>
      {!!enabledControls.TABS && (
        <TabsS>
          <LogoWrapperS>
            <img src="/teempla/build/img/intro-logo.png" alt="Teempla Logo" />
          </LogoWrapperS>
          {Array.from(documents.keys()).map((document, index) => (
            <TabS
              key={index} // Using index as a key if document is not unique
              onClick={() => changeDocument(document)}
              style={
                document === fileReaderInfo.file.name
                  ? {
                      backgroundColor: 'rgba(0,0,0,0.1)',
                    }
                  : {}
              }
            >
              {document}
            </TabS>
          ))}
        </TabsS>
      )}
      <WhiteBoardS ref={whiteboardRef}>
        {/* Context Panel - positioned at a stable location */}
        {contextPanel.visible && (
          <ContextPanel
            visible={true}
            selectedObject={contextPanel.selectedObject}
            onLayerAction={handleLayerAction}
            onPropertyChange={handlePropertyChange}
            onClose={() => setContextPanel(prev => ({ ...prev, visible: false }))}
            board={boardRef.current} // Pass the board instance
          />
        )}
        {/* Stamp Palette */}
        {canvasDrawingSettings.currentMode === modes.STAMP && (
          <StampPalette
            onSelectStamp={(stampName, customStamp) => {
              setSelectedStamp(stampName);
              if (board?.currentTool) {
                board.currentTool.setCurrentStamp(stampName, customStamp);
              }
            }}
            selectedStamp={selectedStamp}
          />
        )}
        {/* Object Calculator */}
        {canvasDrawingSettings.currentMode === modes.OBJECT_CALCULATOR && (
          <ObjectCalculator
            board={board}
            onClose={() => changeMode(modes.SELECT)}
          />
        )}
        <ToolbarHolderS>
          <ColorBarS>
            {!!enabledControls.COLOR_PICKER && (
              <ToolbarItemS>
                <ColorPicker
                  size={28}
                  color={canvasDrawingSettings.currentColor}
                  onChange={changeCurrentColor}
                ></ColorPicker>
              </ToolbarItemS>
            )}
            {!!enabledControls.BRUSH && (
              <ToolbarItemS>
                <RangeInputS
                  type="range"
                  min={1}
                  max={30}
                  step={1}
                  thumbcolor={canvasDrawingSettings.currentColor}
                  value={canvasDrawingSettings.brushWidth}
                  onChange={changeBrushWidth}
                />
              </ToolbarItemS>
            )}
            {!!enabledControls.DEFAULT_COLORS && (
              <>{getColorButtons(['#6161ff', '#ff4f4f', '#3fd18d', '#ec70ff', '#000000'])}</>
            )}
            {!!enabledControls.FILL && (
              <ButtonS
                type="button"
                className={canvasDrawingSettings.fill ? 'selected' : ''}
                onClick={changeFill}
              >
                <img src={FillIcon} alt="Delete" />
              </ButtonS>
            )}
          </ColorBarS>
          <ToolbarS>
            {getControls()}
            
            {!!enabledControls.UNDO_REDO && (
              <>
                <SeparatorS />
                
                <ToolbarItemS>
                  <ButtonS 
                    type="button" 
                    onClick={handleUndo} 
                    title="Undo"
                    style={{ 
                      opacity: canUndo && !isUndoing ? 1 : 0.5, 
                      cursor: canUndo && !isUndoing ? 'pointer' : 'not-allowed',
                      animation: isUndoing ? 'pulse 1s infinite' : 'none'
                    }}
                    disabled={!canUndo || isUndoing}
                  >
                    <img src={UndoIcon} alt="Undo" />
                  </ButtonS>
                </ToolbarItemS>
                
                <ToolbarItemS>
                  <ButtonS 
                    type="button" 
                    onClick={handleRedo} 
                    title="Redo"
                    style={{ 
                      opacity: canRedo && !isRedoing ? 1 : 0.5, 
                      cursor: canRedo && !isRedoing ? 'pointer' : 'not-allowed',
                      animation: isRedoing ? 'pulse 1s infinite' : 'none'
                    }}
                    disabled={!canRedo || isRedoing}
                  >
                    <img src={RedoIcon} alt="Redo" />
                  </ButtonS>
                </ToolbarItemS>
                

              </>
            )}

            {!!enabledControls.CLEAR && (
              <ButtonS type="button" onClick={() => board.clearCanvas()}>
                <img src={DeleteIcon} alt="Delete" />
              </ButtonS>
            )}

            <SeparatorS />

            {!!enabledControls.FILES && (
              <ToolbarItemS>
                <input
                  ref={uploadPdfRef}
                  hidden
                  accept="image/*,.pdf"
                  type="file"
                  onChange={onFileChange}
                />
                <ButtonS onClick={() => uploadPdfRef.current.click()}>
                  <img src={UploadIcon} alt="Delete" />
                </ButtonS>
              </ToolbarItemS>
            )}

            {!!enabledControls.SAVE_AS_IMAGE && (
              <ToolbarItemS>
                <ButtonS onClick={handleSaveCanvasAsImage}>
                  <img src={DownloadIcon} alt="Download" />
                </ButtonS>
              </ToolbarItemS>
            )}

            {!!enabledControls.GO_TO_START && (
              <ToolbarItemS>
                <ButtonS onClick={bringControlTOStartPosition}>
                  <img src={Recenter} alt="Recenter" />
                </ButtonS>
              </ToolbarItemS>
            )}

            {!!enabledControls.SAVE_AND_LOAD && (
              <ToolbarItemS>
                <ButtonS type="button" onClick={handleSaveCanvasState}>
                  Save
                </ButtonS>
              </ToolbarItemS>
            )}

            {!!enabledControls.SAVE_AND_LOAD && canvasSaveData && canvasSaveData.length > 0 && (
              <ToolbarItemS>
                <ButtonS onClick={() => handleLoadCanvasState(canvasSaveData[0])}>Load</ButtonS>
              </ToolbarItemS>
            )}
          </ToolbarS>
          <ZoomBarS>
            {!!enabledControls.ZOOM && (
              <ToolbarItemS>
                <ButtonS onClick={handleZoomIn} title="Zoom In">
                  <img src={ZoomInIcon} alt="Zoom In" />
                </ButtonS>
              </ToolbarItemS>
            )}

            {!!enabledControls.ZOOM && (
              <ToolbarItemS>
                <ButtonS onClick={handleResetZoom} title="Reset Zoom">
                  <span style={{ fontSize: '11px' }}>{Math.floor(zoom * 100)}%</span>
                </ButtonS>
              </ToolbarItemS>
            )}

            {!!enabledControls.ZOOM && (
              <ToolbarItemS>
                <ButtonS onClick={handleZoomOut} title="Zoom Out">
                  <img src={ZoomOutIcon} alt="Zoom Out" />
                </ButtonS>
              </ToolbarItemS>
            )}
          </ZoomBarS>
        </ToolbarHolderS>

        <canvas style={{ zIndex: 1 }} ref={canvasRef} id="canvas" />

        {!!fileReaderInfo?.file?.size && (
          <PDFWrapperS>
            <PdfReader
              fileReaderInfo={fileReaderInfo}
              onPageChange={handlePageChange}
              updateFileReaderInfo={updateFileReaderInfo}
            />
          </PDFWrapperS>
        )}
      </WhiteBoardS>
    </WrapperS>
  );
};

Whiteboard.propTypes = {
  aspectRatio: PropTypes.number,
};

export default Whiteboard;
